import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DraggableAttributes,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Group, Stack } from '@mantine/core'
import { TDashboard } from 'api/dashboards/getDashboard'
import { ChartFilters } from 'components/chart/filters/ChartFilters'
import { PublishDashboardButton } from 'components/dashboards/PublishDashboardButton'
import { ExtendedChartsViewSwitch } from 'components/devices/ExtendedChartsViewSwitch'
import dayjs from 'dayjs'
import { createContext, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BiCustomize } from 'react-icons/bi'
import { useParams, useSearchParams } from 'react-router-dom'
import { NoDataPrompt } from '..'
import { AddRowButton } from './AddRowButton'
import { DashboardCustomizer } from './DashboardCustomizer'
import { DashboardRow } from './DashboardRow'
import { DashboardSelector } from './DashboardSelector'
import { ExportDashboardToCSVButton } from './ExportDashboardToCSVButton'
import {
  useDashboardActions,
  useDashboardStore,
} from './hooks/useDashboardStore'
import { useReorderColumns } from './hooks/useReorderColumns'
import { useReorderRows } from './hooks/useReorderRows'

const MEASURE_ALWAYS = 0

export const DashboardProvider = ({
  data,
  onChange,
}: {
  data: TDashboard
  onChange: () => void
}) => {
  const [urlParams] = useSearchParams()
  const params = Object.fromEntries(urlParams.entries())

  const { deviceId } = useParams<{ deviceId: string }>()
  const { t } = useTranslation()
  const isEditMode = useDashboardStore(state => state.isEditMode)
  const { toggleEditMode } = useDashboardActions()
  const itemIds = useMemo(() => data.rows.map(row => row.id), [data.rows])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const reorderColumns = useReorderColumns({ dashboardId: data.id })
  const reorderRows = useReorderRows({ dashboardId: data.id })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || !over.id) return
    if (active.id === over.id) return
    if (!active.data.current) return

    switch (active.data.current.type) {
      case 'COL':
        reorderColumns({ active, over })
        break
      case 'ROW':
        reorderRows({ active, over })
        break
    }
  }
  return (
    <DashboardContext.Provider value={data}>
      <Stack>
        <Group justify="space-between">
          <DashboardSelector />
          <Group ml={'auto'}>
            <ExtendedChartsViewSwitch />
            <PublishDashboardButton
              dashboardId={data.id}
              deviceId={deviceId}
              isPublicDashboard={data.isPublic}
            />
            <ExportDashboardToCSVButton
              id={data.id}
              name={data?.name || ''}
              dashboard={data}
              fromDate={
                params?.fromDate || dayjs().subtract(24, 'hour').toISOString()
              }
              toDate={params?.toDate || dayjs().toISOString()}
            />
            <ChartFilters />
            <DashboardCustomizer onChange={onChange} />
          </Group>
        </Group>
        <DndContext
          measuring={{
            droppable: {
              strategy: MEASURE_ALWAYS,
            },
          }}
          modifiers={[restrictToParentElement]}
          onDragEnd={handleDragEnd}
          collisionDetection={closestCorners}
          sensors={sensors}
        >
          <SortableContext
            strategy={verticalListSortingStrategy}
            items={itemIds}
          >
            {data.rows.map(row => (
              <DashboardRow key={row.id} {...row} />
            ))}
            {data.rows.length === 0 && !isEditMode && (
              <NoDataPrompt
                title={t('dashboard.createCTA')}
                buttonContent={t('dashboard.edit')}
                onClick={() => toggleEditMode()}
                icon={BiCustomize}
              />
            )}
            <AddRowButton />
          </SortableContext>
        </DndContext>
      </Stack>
    </DashboardContext.Provider>
  )
}

export type SortableItemProps = {
  listeners?: SyntheticListenerMap
  attributes?: DraggableAttributes
  style?: Record<string, string | number | undefined>
}

const DashboardContext = createContext<TDashboard | null>(null)
export const useDashboardContext = () => {
  const dashboard = useContext(DashboardContext)
  if (dashboard === null) throw new Error('Missing dashboard info')

  return dashboard
}
